import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { User } from "../types/User";
import { getCustomerCamarasByCustomerId } from "../api/Camara.api.js";
import { getObras } from "../../obras/api/obras.api.js";
import { Obra } from "../../obras/types/Obra";
import { useParams } from "react-router-dom";

// --- FUNCIONES DE VALIDACIÓN ---
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?[0-9]{7,15}$/;
  return phoneRegex.test(phone);
};

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData?: User;
  onConfirm: (user: User) => void;
}

const roleOptions = [
  { value: "guest", label: "Invitado" },
  { value: "responsibleUser", label: "Usuario Responsable" },
  { value: "defaultCustomerUser", label: "Usuario Estándar" },
];

const notificationTypeOptions = [
  { value: "timelapseReports", label: "Reportes de Timelapse" },
  { value: "camaraOff", label: "Cámara Apagada" },
];

// Normaliza `obra`: soporta el formato legacy (string único) además del array actual.
const toObraArray = (obra: unknown): string[] => {
  if (Array.isArray(obra)) return obra;
  if (typeof obra === "string" && obra) return [obra];
  return [];
};

const areaOptions = [
  { value: "marketing", label: "Marketing" },
  { value: "administracion", label: "Administración" },
  { value: "seguridadHigiene", label: "Seguridad e Higiene" },
  { value: "operativo", label: "Operativo" },
  { value: "principal", label: "Principal" },
];

const UserModal = ({ isOpen, onClose, userData, onConfirm }: UserModalProps) => {
  const defaultUser: User = {
    active: true,
    createdAt: new Date().toISOString(),
    customerId: "",
    email: "",
    identifier: "",
    name: "",
    notificationsType: [],
    password: "",
    permissions: [],
    phoneNumber: "",
    role: "",
    surname: "",
    obra: [],
  };

  const [user, setUser] = useState<User>(defaultUser);
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [availablePermissions, setAvailablePermissions] = useState<{ identifier: string; Name: string }[]>([]);
  const [obras, setObras] = useState<Obra[]>([]);

  const isNewUser = !userData;
  const params = useParams();
  const customerId = params.customerId;

  const getCustomerCamaras = async (id: string | undefined): Promise<void> => {
    try {
      const response = await getCustomerCamarasByCustomerId(id || "");
      if (response) {
        setAvailablePermissions(response);
      }
    } catch (error) {
      console.error("Error al obtener las cámaras:", error);
    }
  };

  const getCustomerObras = async (id: string | undefined): Promise<void> => {
    try {
      const response = await getObras(id || "");
      if (response) {
        setObras(response);
      }
    } catch (error) {
      console.error("Error al obtener las obras:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (userData) {
        setUser({ ...defaultUser, ...userData, obra: toObraArray(userData.obra) });
      } else {
        setUser(defaultUser);
        setPasswordConfirmation("");
      }
      getCustomerCamaras(customerId);
      getCustomerObras(customerId);
    }
  }, [isOpen, userData, customerId]);

  useEffect(() => {
    if (!availablePermissions.length) return;
    if (user.role === "defaultCustomerUser") {
      setUser((prev) => ({
        ...prev,
        permissions: availablePermissions.map((p) => p.identifier),
      }));
    }
  }, [user.role, availablePermissions]);

  const handleChangeField = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: keyof User) => {
    if (field === "active") {
      const value = e.target.value === "true";
      setUser((prev) => ({ ...prev, [field]: value }));
    } else {
      setUser((prev) => ({ ...prev, [field]: e.target.value }));
    }
  };

  const handleArrayChange = (value: string, field: "permissions" | "notificationsType" | "obra") => {
    setUser((prev) => {
      const currentArray = (prev[field] as string[]) || [];
      if (currentArray.includes(value)) {
        return { ...prev, [field]: currentArray.filter((v) => v !== value) };
      } else {
        return { ...prev, [field]: [...currentArray, value] };
      }
    });
  };

  const handleSubmit = () => {
    if (!user.email || !user.name || !user.surname || !user.role) {
      toast.error("Nombre, apellido, email y rol son obligatorios.");
      return;
    }

    if (!validateEmail(user.email)) {
      toast.error("El formato del email es incorrecto.");
      return;
    }

    if (user.phoneNumber && !validatePhoneNumber(user.phoneNumber)) {
      toast.error("El número de teléfono es incorrecto.");
      return;
    }

    if (isNewUser) {
      if (!user.password || user.password.length < 6) {
        toast.error("La contraseña debe tener al menos 6 caracteres.");
        return;
      }
      if (user.password !== passwordConfirmation) {
        toast.error("La contraseña y su confirmación no coinciden.");
        return;
      }
    }

    const userToSubmit: User = {
      ...user,
      password: isNewUser ? user.password : userData?.password || "",
    };

    onConfirm(userToSubmit);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-0 md:p-4 z-50 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-none md:rounded-xl w-full h-full md:h-auto max-w-5xl md:mt-16 md:mb-8 shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center border-b p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-bold">
            {userData ? "✏️ Editar usuario" : "👤 Crear usuario"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl md:text-xl">✕</button>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6 space-y-6 md:space-y-8 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <h3 className="col-span-full text-lg font-semibold text-blue-600 border-b pb-2">Información Personal</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre*</label>
              <input type="text" value={user.name} onChange={(e) => handleChangeField(e, "name")} className="w-full border rounded-lg px-3 py-2" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Apellido*</label>
              <input type="text" value={user.surname} onChange={(e) => handleChangeField(e, "surname")} className="w-full border rounded-lg px-3 py-2" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email*</label>
              <input type="email" value={user.email} onChange={(e) => handleChangeField(e, "email")} className="w-full border rounded-lg px-3 py-2" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Teléfono</label>
              <input type="tel" value={user.phoneNumber} onChange={(e) => handleChangeField(e, "phoneNumber")} placeholder="+54911..." className="w-full border rounded-lg px-3 py-2" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Rol*</label>
              <select value={user.role} onChange={(e) => handleChangeField(e, "role")} className="w-full border rounded-lg px-3 py-2 bg-white">
                <option value="" disabled>Seleccionar rol</option>
                {roleOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado*</label>
              <select value={user.active ? "true" : "false"} onChange={(e) => handleChangeField(e, "active")} className="w-full border rounded-lg px-3 py-2 bg-white">
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Área</label>
              <select value={user.area || ""} onChange={(e) => handleChangeField(e, "area")} className="w-full border rounded-lg px-3 py-2 bg-white">
                <option value="">Sin área asignada</option>
                {areaOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sección de Contraseña: Solo visible para nuevos usuarios */}
          {isNewUser && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <div className="border p-4 rounded-lg bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Contraseña*</label>
                <input
                  type="password"
                  value={user.password}
                  onChange={(e) => handleChangeField(e, "password")}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div className="border p-4 rounded-lg bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirmar contraseña*</label>
                <input
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                />
                {passwordConfirmation && user.password && user.password !== passwordConfirmation && (
                  <p className="text-red-500 text-xs mt-1">⚠️ Las contraseñas no coinciden.</p>
                )}
              </div>
            </div>
          )}

          {/* Permisos y Notificaciones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
            {user.role !== "responsibleUser" && (
              <div className="border p-4 rounded-lg bg-green-50">
                <h4 className="font-semibold mb-3">Acceso a cámaras</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {availablePermissions.map((p) => (
                    <label key={p.identifier} className="flex items-center text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={user.permissions.includes(p.identifier)}
                        onChange={() => handleArrayChange(p.identifier, "permissions")}
                        className="mr-2 h-4 w-4 text-blue-600"
                      />
                      {p.Name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="border p-4 rounded-lg bg-blue-50">
              <h4 className="font-semibold mb-3">Notificaciones</h4>
              <div className="space-y-2">
                {notificationTypeOptions.map((opt) => (
                  <label key={opt.value} className="flex items-center text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={user.notificationsType.includes(opt.value)}
                      onChange={() => handleArrayChange(opt.value, "notificationsType")}
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {obras.length > 0 && (
              <div className="border p-4 rounded-lg bg-purple-50">
                <h4 className="font-semibold mb-3">Obras</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {obras.map((o) => (
                    <label key={o.id} className="flex items-center text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(user.obra || []).includes(o.id)}
                        onChange={() => handleArrayChange(o.id, "obra")}
                        className="mr-2 h-4 w-4 text-blue-600"
                      />
                      {o.nombre}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 border-t p-4 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-6 py-2 bg-gray-300 rounded-lg font-medium">Cancelar</button>
          <button onClick={handleSubmit} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium">
            {userData ? "Actualizar usuario" : "Crear usuario"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserModal;